import React, { useState } from 'react';
import { Gift, Star, ShoppingBag, Heart, TreePine, Zap } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useRewards } from '../hooks/useRewards';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

export default function Rewards() {
  const [filter, setFilter] = useState<'all' | 'discount' | 'donation' | 'badge' | 'experience'>('all');
  const [claimedRewards, setClaimedRewards] = useState<Set<string>>(new Set());
  
  const { rewards, loading } = useRewards();
  const { profile } = useAuth();

  if (loading || !profile) {
    return <LoadingSpinner />;
  }

  const filteredRewards = filter === 'all' 
    ? rewards 
    : rewards.filter(reward => reward.category === filter);

  const handleClaim = (rewardId: string, cost: number) => {
    if (profile.eco_coins >= cost && !claimedRewards.has(rewardId)) {
      setClaimedRewards(new Set([...claimedRewards, rewardId]));
      // In a real app, this would update the user's coin balance
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'discount': return 'bg-blue-100 text-blue-800';
      case 'donation': return 'bg-green-100 text-green-800';
      case 'badge': return 'bg-purple-100 text-purple-800';
      case 'experience': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Rewards Store</h2>
        <p className="text-gray-600 mb-6">
          Turn your EcoCoins into real-world impact and exclusive rewards
        </p>
        
        {/* User Balance */}
        <div className="inline-flex items-center space-x-3 bg-emerald-50 px-6 py-3 rounded-full">
          <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
          <span className="text-lg font-bold text-emerald-700">
            {profile.eco_coins.toLocaleString()} EcoCoins Available
          </span>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap justify-center gap-2">
        {[
          { key: 'all', label: 'All Rewards' },
          { key: 'discount', label: 'Discounts' },
          { key: 'donation', label: 'Donations' },
          { key: 'badge', label: 'Badges' },
          { key: 'experience', label: 'Experiences' }
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key as any)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === tab.key
                ? 'bg-emerald-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Rewards Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredRewards.map((reward) => {
          const IconComponent = (LucideIcons as any)[reward.icon] || Gift;
          const isClaimed = claimedRewards.has(reward.id);
          const canAfford = profile.eco_coins >= reward.cost;
          
          return (
            <div
              key={reward.id}
              className={`bg-white rounded-2xl border overflow-hidden hover:shadow-lg transition-all duration-300 ${
                isClaimed ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 hover:-translate-y-1'
              }`}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={reward.image_url}
                  alt={reward.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(reward.category)}`}>
                    {reward.category}
                  </span>
                </div>
                {isClaimed && (
                  <div className="absolute inset-0 bg-emerald-500 bg-opacity-90 flex items-center justify-center">
                    <div className="text-center text-white">
                      <Gift className="h-12 w-12 mx-auto mb-2" />
                      <p className="font-bold">Claimed!</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <IconComponent className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">{reward.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{reward.description}</p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                      <span className="text-lg font-bold text-gray-900">
                        {reward.cost.toLocaleString()} EcoCoins
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {reward.available} available
                    </p>
                  </div>
                  
                  {isClaimed ? (
                    <div className="bg-emerald-100 text-emerald-700 px-4 py-2 rounded-lg font-medium">
                      Claimed ✓
                    </div>
                  ) : (
                    <button
                      onClick={() => handleClaim(reward.id, reward.cost)}
                      disabled={!canAfford}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        canAfford
                          ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                          : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {canAfford ? 'Claim Reward' : 'Not Enough Coins'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}